// A limitation's extension, shown in its Remaining cell. Aaron 2026-10-07 ("Keep
// extension; fix colour", then fleet-wide): an extension no longer turns a row green --
// the row colours by the extended due -- so this marker is what keeps an extension
// visible, on every limitation surface (components, inspections, ADs).
//
// Legacy showed it as the time-to-run cell's "Current Extension / Reason" title
// (mod_aircraft/ajax/aircraft_components_tab.inc:1134-1136). Shape: PATTERNS BP-48's
// `Chip size="small"` (outlined, default colour -- not a status), the detail in its
// tooltip and accessible name, focusable so keyboard and touch reach it.
// No Figma node is mapped for this marker yet (not verified).
import React from "react";
import { Chip } from "@mui/material";
import LightTooltip from "./LightTooltip";

/**
 * ON EXTENSION -- the warning colour of a limitation flying on a positive extension:
 * past its original due, not yet past next + extension. Aaron 2026-10-07: "should be
 * the aeronet logo amber ... Amber stops it being red as it's under extension until
 * the extension no longer applies". Stored as 'amber' (app inspections.completion.
 * extension_due_colour); ranked red > amber > blue.
 *
 * Colour: the logo amber token --an-orange (P2c-amber-everywhere) -- 3.45:1 on
 * white, the compliance WARNING chip's bar (flight-duty/components/warnChip.ts). Text in
 * it is never bold (Aaron 2026-10-07: "need to remove all the bold").
 */
export const ON_EXTENSION = "amber";
export const ON_EXTENSION_LABEL = "On extension";
export const ON_EXTENSION_COLOUR = "var(--an-orange)";

export interface ExtensionSource {
  based_on?: string | null;
  extension?: number | string | null;
  warning_unit?: string | null;
  extension_reason?: string | null;
}

export interface ExtensionMarker {
  /** The chip's claim, signed: "Ext +300", "Ext −11,000". */
  label: string;
  /** The detail: the extension and its unit, where it is counted, and the reason. */
  title: string;
}

export interface ExtensionMarkerOptions {
  /** Axes with no due point (aircraft.condition_based_on_list): never marked. */
  conditionBasedOn?: string[];
  /** The columns the extension is already counted in, e.g. "Remove and Remaining". */
  countedIn?: string;
}

const EXTENSION_NUMBER = new Intl.NumberFormat("en-NZ", { maximumFractionDigits: 2 });

/** Null for no extension (0 / blank), or a condition-based row. Any non-zero value is
 *  marked, negative included (legacy titled only a positive one). */
export function extensionMarker(
  source: ExtensionSource | null | undefined,
  { conditionBasedOn = [], countedIn = "Remaining" }: ExtensionMarkerOptions = {}
): ExtensionMarker | null {
  if (!source) return null;
  const basedOn = source.based_on || "";
  if (conditionBasedOn.includes(basedOn)) return null;
  const value = Number(source.extension);
  if (!value || Number.isNaN(value)) return null;
  const signed = `${value > 0 ? "+" : "−"}${EXTENSION_NUMBER.format(Math.abs(value))}`;
  const unit = basedOn.includes("Date") ? "days" : String(source.warning_unit ?? "").trim();
  const reason = String(source.extension_reason ?? "").trim();
  const title = [
    `Extension ${signed}${unit ? ` ${unit}` : ""}, counted in ${countedIn}.`,
    reason ? `Reason: ${reason}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  return { label: `Ext ${signed}`, title };
}

export interface ExtensionChipProps extends ExtensionMarkerOptions {
  source: ExtensionSource | null | undefined;
}

/** Renders nothing when the row holds no extension. */
export default function ExtensionChip({ source, ...options }: ExtensionChipProps) {
  const marker = extensionMarker(source, options);
  if (!marker) return null;
  return (
    <LightTooltip title={marker.title}>
      <Chip
        size="small"
        variant="outlined"
        label={marker.label}
        tabIndex={0}
        aria-label={marker.title}
        data-testid="extension-marker"
      />
    </LightTooltip>
  );
}
